import { useEffect, useState } from "react";
import { Routes, Route, NavLink, useNavigate, Link } from "react-router-dom";
import CalendarConnections from "@/pages/CalendarConnections";
import { useAuth } from "@/context/AuthContext";
import { supabase, apiFetch } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import BillingTab from "@/pages/settings/BillingTab";
import { PageHeader } from "@/lib/ui";
import { saveOrShareBlob, fetchAndSave } from "@/lib/downloads";

const BUSINESS_TYPES = [
  "Healthcare / Behavioral health", "Legal services", "Consulting", "Coaching",
  "Insurance", "Social services / Nonprofit", "Property management", "Other",
];
const TIMEZONES = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Anchorage", "Pacific/Honolulu", "America/Toronto", "America/Vancouver",
  "Europe/London", "Europe/Berlin", "Australia/Sydney",
];

function SettingsNav() {
  const { currentRole } = useAuth();
  const links = [
    { to: "/app/settings", label: "My profile", end: true, testid: "sn-profile" },
    { to: "/app/settings/security", label: "Security", testid: "sn-security" },
    { to: "/app/settings/account", label: "Account", testid: "sn-account" },
    { to: "/app/settings/integrations", label: "Integrations", testid: "sn-integrations" },
    ...(currentRole === "org_admin" ? [
      { to: "/app/settings/organization", label: "Organization", testid: "sn-org" },
      { to: "/app/settings/preferences", label: "Preferences", testid: "sn-prefs" },
      { to: "/app/settings/billing", label: "Billing", testid: "sn-billing" },
      { to: "/app/settings/audit", label: "Audit log", testid: "sn-audit" },
      { to: "/app/settings/data", label: "Data & privacy", testid: "sn-data" },
    ] : []),
  ];
  return (
    <nav className="flex gap-1 border-b border-[#E8E2D9] mb-8 overflow-x-auto">
      {links.map((l) => (
        <NavLink key={l.to} to={l.to} end={l.end} data-testid={l.testid}
                 className={({ isActive }) => `px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
                   isActive ? "border-[#E8712C] text-[#E8712C]" : "border-transparent text-[#57534E] hover:text-[#1C1917]"}`}>
          {l.label}
        </NavLink>
      ))}
    </nav>
  );
}

function ProfileTab() {
  const { user, profile, refresh } = useAuth();
  const [fname, setFname] = useState("");
  const [lname, setLname] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (profile?.full_name) {
      const parts = profile.full_name.split(" ");
      setFname(parts[0] || ""); setLname(parts.slice(1).join(" ") || "");
    }
    supabase.from("user_preferences").select("phone").eq("user_id", user.id).limit(1).then(({ data }) => {
      if (data?.[0]) setPhone(data[0].phone || "");
    });
  }, [profile, user.id]);

  const save = async () => {
    setSaving(true);
    const full = `${fname.trim()} ${lname.trim()}`.trim();
    await supabase.from("profiles").update({ full_name: full }).eq("id", user.id);
    // Upsert preferences
    const { data } = await supabase.from("user_preferences").select("user_id").eq("user_id", user.id).limit(1);
    if (data?.[0]) await supabase.from("user_preferences").update({ phone: phone || null }).eq("user_id", user.id);
    else await supabase.from("user_preferences").insert({ user_id: user.id, phone: phone || null });
    await refresh();
    setSaving(false);
    toast.success("Profile updated");
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="profile-tab">
      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6">
        <h3 className="font-display font-semibold text-lg tracking-tight mb-1">My profile</h3>
        <p className="text-sm text-[#57534E] mb-5">Your personal info visible to your team.</p>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>First name</Label><Input data-testid="pf-first" value={fname} onChange={(e) => setFname(e.target.value)} className="mt-1.5" /></div>
          <div><Label>Last name</Label><Input data-testid="pf-last" value={lname} onChange={(e) => setLname(e.target.value)} className="mt-1.5" /></div>
        </div>
        <div className="mt-3"><Label>Email</Label><Input value={user?.email || ""} disabled className="mt-1.5 bg-[#FAF8F5]" /></div>
        <div className="mt-3"><Label>Phone</Label><Input data-testid="pf-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5" placeholder="(555) 123-4567" /></div>
        <Button data-testid="save-profile" onClick={save} disabled={saving} className="mt-5 rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">{saving ? "Saving…" : "Save changes"}</Button>
      </div>
    </div>
  );
}

function AccountTab() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [confirmText, setConfirmText] = useState("");
  const [password, setPassword] = useState("");
  const [transferTo, setTransferTo] = useState("none");
  const [candidates, setCandidates] = useState([]);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiFetch("/account/delete/preview").then(setStatus).catch(() => {});
    apiFetch("/account/delete/transfer-candidates").then((d) => setCandidates(d.candidates || [])).catch(() => {});
  }, []);

  const del = async () => {
    if (confirmText !== "DELETE MY ACCOUNT") return toast.error('Type "DELETE MY ACCOUNT" to confirm');
    if (!password) return toast.error("Enter your current password");
    if (!window.confirm("Are you absolutely sure? This action cannot be undone.")) return;
    setBusy(true);
    try {
      const r = await apiFetch("/account/delete", {
        method: "POST",
        body: JSON.stringify({ password, transfer_owner_to: transferTo === "none" ? null : transferTo }),
      });
      toast.success(r.deleted ? "Account deleted. Signing you out." : "Deletion scheduled.");
      setTimeout(() => signOut().then(() => navigate("/")), 1500);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="account-tab">
      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6">
        <h3 className="font-display font-semibold text-lg tracking-tight mb-1">Account details</h3>
        <p className="text-sm text-[#57534E]">Signed in as <b>{user?.email}</b>.</p>
        <p className="text-sm text-[#57534E] mt-2">Read our <Link to="/privacy" className="text-[#E8712C] underline">Privacy Policy</Link>, <Link to="/terms" className="text-[#E8712C] underline">Terms</Link>, and <Link to="/data-deletion" className="text-[#E8712C] underline">Data Deletion Policy</Link>.</p>
      </div>

      <div className="bg-white rounded-2xl border-2 border-[#F0C69A] p-6" data-testid="danger-zone">
        <h3 className="font-display font-semibold text-lg tracking-tight mb-1 text-[#B02222]">Delete my account</h3>
        <p className="text-sm text-[#57534E] mb-4">
          This permanently deletes your ClientDesk account and personal data. Financial records (invoices, receipts) are retained for 7 years per tax law, stripped of personally identifiable information.
        </p>

        {status && (
          <div className="bg-[#FAF8F5] rounded-lg p-4 mb-4 text-sm space-y-1">
            <div><b>Organizations you belong to:</b> {status.memberships?.length || 0}</div>
            <div><b>Where you're the sole admin:</b> {status.sole_admin_orgs?.length || 0}</div>
            <div><b>Active calendar connections:</b> {status.calendar_connections || 0}</div>
            <div><b>Active Stripe subscriptions to cancel:</b> {status.active_subscriptions || 0}</div>
            {status.sole_admin_orgs?.length > 0 && (
              <div className="mt-2 text-[#B85C1F]">
                You are the sole admin of {status.sole_admin_orgs.length} organization(s). Choose an admin to transfer ownership to below, or their organizations will be fully deleted.
              </div>
            )}
          </div>
        )}

        {candidates.length > 0 && (
          <div className="mb-4">
            <Label>Transfer ownership to (optional)</Label>
            <Select value={transferTo} onValueChange={setTransferTo}>
              <SelectTrigger className="mt-1.5" data-testid="transfer-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Do not transfer, delete organization(s) —</SelectItem>
                {candidates.map((c) => (
                  <SelectItem key={c.user_id} value={c.user_id}>{c.full_name || c.email} ({c.organization_name})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <Label>Confirm your password</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" data-testid="delete-pw" />
          </div>
          <div>
            <Label>Type <b className="font-mono">DELETE MY ACCOUNT</b> to confirm</Label>
            <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} className="mt-1.5" data-testid="delete-confirm" />
          </div>
          <Button
            onClick={del}
            disabled={busy || confirmText !== "DELETE MY ACCOUNT" || !password}
            data-testid="delete-account-btn"
            className="rounded-full bg-[#B02222] hover:bg-[#8C1B1B] text-white disabled:opacity-40"
          >
            {busy ? "Deleting…" : "Permanently delete my account"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SecurityTab() {
  const [current, setCurrent] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { signOut } = useAuth();

  const change = async () => {
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw !== pw2) return toast.error("Passwords don't match");
    setSaving(true);
    const { error } = await supabase.auth.updateUser({ password: pw });
    setSaving(false);
    if (error) return toast.error(error.message);
    setCurrent(""); setPw(""); setPw2("");
    toast.success("Password updated");
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="security-tab">
      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6">
        <h3 className="font-display font-semibold text-lg tracking-tight mb-1">Change password</h3>
        <p className="text-sm text-[#57534E] mb-5">Use at least 8 characters.</p>
        <div className="space-y-3">
          <div><Label>New password</Label><Input type="password" data-testid="sec-pw" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1.5" /></div>
          <div><Label>Confirm password</Label><Input type="password" data-testid="sec-pw2" value={pw2} onChange={(e) => setPw2(e.target.value)} className="mt-1.5" /></div>
        </div>
        <Button data-testid="sec-save" onClick={change} disabled={saving || !pw} className="mt-5 rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">{saving ? "Saving…" : "Update password"}</Button>
      </div>
      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6">
        <h3 className="font-display font-semibold text-lg tracking-tight mb-1">Session</h3>
        <p className="text-sm text-[#57534E] mb-5">Sign out of ClientDesk on this device.</p>
        <Button data-testid="sec-signout" variant="outline" onClick={() => signOut().then(() => navigate("/login"))} className="rounded-full">Sign out</Button>
      </div>
    </div>
  );
}

function OrganizationTab() {
  const { currentOrg, currentOrgId, refresh } = useAuth();
  const [name, setName] = useState("");
  const [prefs, setPrefs] = useState({ business_type: "", phone: "", email: "", website: "", address: "", time_zone: "America/New_York" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(currentOrg?.name || "");
    supabase.from("org_preferences").select("*").eq("organization_id", currentOrgId).limit(1).then(({ data }) => {
      if (data?.[0]) setPrefs({
        business_type: data[0].business_type || "",
        phone: data[0].phone || "", email: data[0].email || "",
        website: data[0].website || "", address: data[0].address || "",
        time_zone: data[0].time_zone || "America/New_York",
      });
    });
  }, [currentOrg, currentOrgId]);

  const save = async () => {
    setSaving(true);
    await supabase.from("organizations").update({ name }).eq("id", currentOrgId);
    const { data } = await supabase.from("org_preferences").select("organization_id").eq("organization_id", currentOrgId).limit(1);
    const payload = { ...prefs };
    Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
    if (data?.[0]) await supabase.from("org_preferences").update(payload).eq("organization_id", currentOrgId);
    else await supabase.from("org_preferences").insert({ organization_id: currentOrgId, ...payload });
    await refresh();
    setSaving(false);
    toast.success("Organization updated");
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="organization-tab">
      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6 space-y-4">
        <h3 className="font-display font-semibold text-lg tracking-tight">Organization details</h3>
        <div><Label>Organization name</Label><Input data-testid="org-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" /></div>
        <div>
          <Label>Business type</Label>
          <Select value={prefs.business_type} onValueChange={(v) => setPrefs({...prefs, business_type: v})}>
            <SelectTrigger className="mt-1.5"><SelectValue placeholder="Choose…" /></SelectTrigger>
            <SelectContent>{BUSINESS_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Phone</Label><Input value={prefs.phone} onChange={(e) => setPrefs({...prefs, phone: e.target.value})} className="mt-1.5" /></div>
          <div><Label>Email</Label><Input value={prefs.email} onChange={(e) => setPrefs({...prefs, email: e.target.value})} className="mt-1.5" /></div>
        </div>
        <div><Label>Website</Label><Input value={prefs.website} onChange={(e) => setPrefs({...prefs, website: e.target.value})} className="mt-1.5" /></div>
        <div><Label>Address</Label><Input value={prefs.address} onChange={(e) => setPrefs({...prefs, address: e.target.value})} className="mt-1.5" /></div>
        <div>
          <Label>Time zone</Label>
          <Select value={prefs.time_zone} onValueChange={(v) => setPrefs({...prefs, time_zone: v})}>
            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>{TIMEZONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button data-testid="save-org" onClick={save} disabled={saving} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">{saving ? "Saving…" : "Save"}</Button>
      </div>
    </div>
  );
}

function PreferencesTab() {
  const { currentOrgId, user } = useAuth();
  const [orgPrefs, setOrgPrefs] = useState({ default_calendar_view: "week", date_format: "MM/DD/YYYY", time_format: "12h" });
  const [userPrefs, setUserPrefs] = useState({ notif_email: true, notif_in_app: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    supabase.from("org_preferences").select("default_calendar_view,date_format,time_format").eq("organization_id", currentOrgId).limit(1).then(({ data }) => {
      if (data?.[0]) setOrgPrefs({
        default_calendar_view: data[0].default_calendar_view || "week",
        date_format: data[0].date_format || "MM/DD/YYYY",
        time_format: data[0].time_format || "12h",
      });
    });
    supabase.from("user_preferences").select("notif_email,notif_in_app").eq("user_id", user.id).limit(1).then(({ data }) => {
      if (data?.[0]) setUserPrefs({ notif_email: !!data[0].notif_email, notif_in_app: !!data[0].notif_in_app });
    });
  }, [currentOrgId, user.id]);

  const save = async () => {
    setSaving(true);
    const { data } = await supabase.from("org_preferences").select("organization_id").eq("organization_id", currentOrgId).limit(1);
    if (data?.[0]) await supabase.from("org_preferences").update(orgPrefs).eq("organization_id", currentOrgId);
    else await supabase.from("org_preferences").insert({ organization_id: currentOrgId, ...orgPrefs });

    const { data: u } = await supabase.from("user_preferences").select("user_id").eq("user_id", user.id).limit(1);
    if (u?.[0]) await supabase.from("user_preferences").update(userPrefs).eq("user_id", user.id);
    else await supabase.from("user_preferences").insert({ user_id: user.id, ...userPrefs });
    setSaving(false);
    toast.success("Preferences saved");
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="prefs-tab">
      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6 space-y-4">
        <h3 className="font-display font-semibold text-lg tracking-tight">Workspace preferences</h3>
        <div>
          <Label>Default calendar view</Label>
          <Select value={orgPrefs.default_calendar_view} onValueChange={(v) => setOrgPrefs({...orgPrefs, default_calendar_view: v})}>
            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="day">Day</SelectItem><SelectItem value="week">Week</SelectItem><SelectItem value="month">Month</SelectItem><SelectItem value="list">List</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Date format</Label>
            <Select value={orgPrefs.date_format} onValueChange={(v) => setOrgPrefs({...orgPrefs, date_format: v})}>
              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem><SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem><SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>Time format</Label>
            <Select value={orgPrefs.time_format} onValueChange={(v) => setOrgPrefs({...orgPrefs, time_format: v})}>
              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="12h">12-hour</SelectItem><SelectItem value="24h">24-hour</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6 space-y-4">
        <h3 className="font-display font-semibold text-lg tracking-tight">Notification preferences</h3>
        <div className="flex items-center justify-between">
          <div><div className="text-sm font-medium">In-app notifications</div><div className="text-xs text-[#57534E]">Show notifications in ClientDesk</div></div>
          <Switch checked={userPrefs.notif_in_app} onCheckedChange={(v) => setUserPrefs({...userPrefs, notif_in_app: v})} data-testid="pref-inapp" />
        </div>
        <div className="flex items-center justify-between">
          <div><div className="text-sm font-medium">Email notifications</div><div className="text-xs text-[#57534E]">Receive important updates by email</div></div>
          <Switch checked={userPrefs.notif_email} onCheckedChange={(v) => setUserPrefs({...userPrefs, notif_email: v})} data-testid="pref-email" />
        </div>
      </div>
      <Button data-testid="save-prefs" onClick={save} disabled={saving} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">{saving ? "Saving…" : "Save preferences"}</Button>
    </div>
  );
}

function DataPrivacyTab() {
  const { currentOrgId, currentRole, session } = useAuth();
  const isOrgAdmin = currentRole === "org_admin";
  const ALL_TYPES = [
    { key: "clients", label: "Clients" },
    { key: "tasks", label: "Tasks" },
    { key: "bookings", label: "Bookings / Appointments" },
    { key: "appointments", label: "Appointments (legacy)" },
    { key: "service_requests", label: "Service Requests" },
    { key: "service_time", label: "Service Time" },
    { key: "forms", label: "Form Responses" },
    { key: "contracts", label: "Contracts" },
    { key: "invoices", label: "Invoices" },
    { key: "notes", label: "Notes" },
  ];
  const [types, setTypes] = useState(ALL_TYPES.map((t) => t.key));
  const [scope, setScope] = useState("all"); // all | selected
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [format, setFormat] = useState("csv"); // csv | zip | json
  const [includeDocs, setIncludeDocs] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!currentOrgId || !isOrgAdmin) return;
    supabase.from("clients").select("id, first_name, last_name")
      .eq("organization_id", currentOrgId).order("first_name")
      .then(({ data }) => setClients(data || []));
    apiFetch(`/data-export/preview?organization_id=${currentOrgId}`).then(setPreview).catch(() => {});
  }, [currentOrgId, isOrgAdmin]);

  const activeTypes = scope === "all" ? ALL_TYPES.map((t) => t.key) : types;
  const toggleType = (k) =>
    setTypes((prev) => prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]);

  const humanizeError = (msg) => {
    if (!msg) return "Export failed";
    const m = String(msg);
    if (m.includes("org_admin_required")) return "Only organization admins can export data.";
    if (m.includes("no_types_selected")) return "Pick at least one data type to export.";
    if (m.includes("invalid_format")) return "That export format is not supported.";
    return m;
  };

  const doExport = async () => {
    if (activeTypes.length === 0) { toast.error("Pick at least one data type"); return; }
    setBusy(true);
    const t = toast.loading("Preparing export… this can take a moment for large workspaces.");
    try {
      // Refresh session BEFORE the blob fetch. Long-idle tabs can carry an
      // expired access_token; apiFetch handles this for JSON responses but the
      // export uses raw fetch (needs the binary blob + Content-Disposition).
      let token = session?.access_token;
      try {
        const { data } = await supabase.auth.getSession();
        if (data?.session?.access_token) token = data.session.access_token;
        // If expiring in <60s, refresh explicitly.
        const exp = data?.session?.expires_at;
        if (exp && exp * 1000 - Date.now() < 60_000) {
          const { data: refreshed } = await supabase.auth.refreshSession();
          if (refreshed?.session?.access_token) token = refreshed.session.access_token;
        }
      } catch { /* fall back to cached token */ }
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/data-export/organization`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          organization_id: currentOrgId,
          format,
          types: activeTypes,
          client_id: clientId !== "all" ? clientId : undefined,
          from_date: from || undefined,
          to_date: to || undefined,
          include_documents: format === "zip" ? includeDocs : false,
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(humanizeError(j.detail || `Export failed (${res.status})`));
      }
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const name = m ? m[1] : `clientdesk-export-${Date.now()}.${format === "csv" && activeTypes.length === 1 ? "csv" : format === "json" ? "json" : "zip"}`;
      await saveOrShareBlob(blob, name,
        { title: "ClientDesk VA — organization export", dialogTitle: "Save or share export" });
      toast.success("Export ready — check your downloads", { id: t });
    } catch (e) {
      toast.error(humanizeError(e?.message) || "Export failed", { id: t });
    } finally { setBusy(false); }
  };

  const requestExport = () => {
    if (format === "zip") setConfirmOpen(true);
    else doExport();
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="data-tab">
      {!isOrgAdmin ? (
        <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6" data-testid="export-admin-only">
          <h3 className="font-display font-semibold text-lg tracking-tight mb-1">Export organization data</h3>
          <p className="text-sm text-[#57534E]">
            Only organization admins can export the workspace's records. Ask an admin on your team if you need a copy of a client's data.
          </p>
        </div>
      ) : (
      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6 space-y-5" data-testid="export-org-card">
        <div>
          <h3 className="font-display font-semibold text-lg tracking-tight mb-1">Export organization data</h3>
          <p className="text-sm text-[#57534E]">Download a copy of your organization's records for backup, reporting, or migration.</p>
        </div>

        {/* Format tiles */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="export-format-tiles">
          {[
            { k: "csv", title: "Spreadsheet", sub: "CSV — best for Excel or Google Sheets" },
            { k: "zip", title: "Full backup", sub: "ZIP with CSVs + authorized files" },
            { k: "json", title: "Raw JSON", sub: "Advanced — for migrations or APIs" },
          ].map((t) => (
            <button key={t.k} onClick={() => setFormat(t.k)}
              data-testid={`export-format-${t.k}`}
              className={`text-left border rounded-2xl p-4 transition-all ${
                format === t.k
                  ? "border-[#E8712C] bg-[#FFF6EE] ring-2 ring-[#E8712C]/20"
                  : "border-[#E8E2D9] bg-white hover:border-[#E8712C]/40"
              }`}>
              <div className="text-sm font-semibold">{t.title}</div>
              <div className="text-xs text-[#57534E] mt-0.5">{t.sub}</div>
            </button>
          ))}
        </div>

        {/* Scope */}
        <div>
          <Label className="text-xs uppercase tracking-widest text-[#57534E]">Data</Label>
          <div className="flex items-center gap-4 mt-1.5 text-sm">
            <label className="flex items-center gap-1.5">
              <input type="radio" checked={scope === "all"} onChange={() => setScope("all")} data-testid="export-scope-all" />
              All organization data
            </label>
            <label className="flex items-center gap-1.5">
              <input type="radio" checked={scope === "selected"} onChange={() => setScope("selected")} data-testid="export-scope-selected" />
              Selected data types
            </label>
          </div>
          {scope === "selected" && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 mt-3" data-testid="export-type-checkboxes">
              {ALL_TYPES.map((t) => (
                <label key={t.key} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={types.includes(t.key)} onChange={() => toggleType(t.key)}
                         data-testid={`export-type-${t.key}`} />
                  <span>{t.label}</span>
                  {preview && preview.counts?.[t.key === "bookings" ? "bookings" : t.key] != null && (
                    <span className="text-[10px] text-[#A8A29E]">({preview.counts[t.key === "bookings" ? "bookings" : t.key]})</span>
                  )}
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Client (optional)</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="mt-1" data-testid="export-client"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All clients</SelectItem>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">From (optional)</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1" data-testid="export-from" />
          </div>
          <div>
            <Label className="text-xs">To (optional)</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1" data-testid="export-to" />
          </div>
        </div>

        {format === "zip" && (
          <label className="flex items-center gap-2 text-sm" data-testid="export-includedocs-label">
            <input type="checkbox" checked={includeDocs} onChange={(e) => setIncludeDocs(e.target.checked)}
                   data-testid="export-includedocs" />
            Include finalized contract PDFs, invoice PDFs, and uploaded documents
          </label>
        )}

        <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t border-[#E8E2D9]">
          <p className="text-[11px] text-[#A8A29E] max-w-md">
            Exports never contain passwords, API keys, OAuth tokens, portal tokens, or other secrets.
            The download is prepared in this session; you won't be logged out.
          </p>
          <Button onClick={requestExport} disabled={busy} data-testid="export-btn"
                  className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F] text-white">
            {busy ? "Preparing…" : format === "zip" ? "Prepare full backup" : "Download export"}
          </Button>
        </div>
      </div>
      )}

      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6">
        <h3 className="font-display font-semibold text-lg tracking-tight mb-1 text-red-600">Delete workspace</h3>
        <p className="text-sm text-[#57534E] mb-4">To permanently close your workspace, please contact support. This action cannot be undone.</p>
        <Button variant="outline" disabled className="rounded-full text-red-600 border-red-200 opacity-60">Request workspace deletion</Button>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="export-confirm-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4 mx-4">
            <h4 className="font-display font-semibold text-lg">Export organization data?</h4>
            <p className="text-sm text-[#57534E]">
              This export may contain confidential client and business information. Store the downloaded file securely and only share it with people you trust.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setConfirmOpen(false)} className="rounded-full" data-testid="export-confirm-cancel">Cancel</Button>
              <Button onClick={() => { setConfirmOpen(false); doExport(); }}
                      className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F] text-white"
                      data-testid="export-confirm-download">
                Download export
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AuditTab() {
  const { currentOrgId } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentOrgId) return;
    setLoading(true);
    apiFetch(`/organizations/${currentOrgId}/audit`).then((d) => { setEvents(d); setLoading(false); })
      .catch((e) => { toast.error(e.message); setLoading(false); });
  }, [currentOrgId]);

  return (
    <div className="space-y-4" data-testid="audit-tab">
      <div>
        <h3 className="font-display font-semibold text-lg tracking-tight">Audit log</h3>
        <p className="text-sm text-[#57534E]">Every important action in your organization, immutably recorded.</p>
      </div>
      <div className="bg-white rounded-2xl border border-[#E8E2D9] overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-[#57534E]">Loading…</div>
        ) : events.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#57534E]">No events yet.</div>
        ) : (
          <ul className="divide-y divide-[#E8E2D9]">
            {events.map((e) => (
              <li key={e.id} className="px-6 py-3 flex items-center gap-3" data-testid={`audit-row-${e.id}`}>
                <div className="w-8 h-8 rounded-full bg-[#FDE9D8] flex items-center justify-center text-[#E8712C] text-[10px] font-bold flex-shrink-0">
                  {(e.profiles?.full_name || "?")[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm"><b className="font-medium">{e.profiles?.full_name || "System"}</b> <span className="text-[#57534E]">· {e.action}</span></div>
                  <div className="text-xs text-[#A8A29E]">{new Date(e.created_at).toLocaleString()}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default function Settings() {
  const { currentRole } = useAuth();
  return (
    <div className="animate-fade-in-up" data-testid="settings-page">
      <PageHeader title="Settings" subtitle="Manage your profile, organization and workspace." />
      <div className="mt-6">
        <SettingsNav />
        <Routes>
          <Route index element={<ProfileTab />} />
          <Route path="security" element={<SecurityTab />} />
          <Route path="account" element={<AccountTab />} />
          <Route path="integrations" element={<CalendarConnections />} />
          <Route path="organization" element={currentRole === "org_admin" ? <OrganizationTab /> : <div className="text-[#57534E]">Admin access required.</div>} />
          <Route path="preferences" element={currentRole === "org_admin" ? <PreferencesTab /> : <div className="text-[#57534E]">Admin access required.</div>} />
          <Route path="billing" element={currentRole === "org_admin" ? <BillingTab /> : <div className="text-[#57534E]">Admin access required.</div>} />
          <Route path="audit" element={currentRole === "org_admin" ? <AuditTab /> : <div className="text-[#57534E]">Admin access required.</div>} />
          <Route path="data" element={currentRole === "org_admin" ? <DataPrivacyTab /> : <div className="text-[#57534E]">Admin access required.</div>} />
        </Routes>
      </div>
    </div>
  );
}
